/**
 * 创作记忆页面
 *
 * 管理用户的记忆信息，包括：
 * - 用户记忆（全局）：偏好、风格、意图、习惯、特殊要求
 * - 工作区记忆（项目级）：写作偏好、写作流程、语言风格、特殊要求
 *
 * 支持查看、新增、编辑、删除记忆条目
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import memoryApi from '../services/memoryApi';
import { MemoryEditModal } from '../components/memory/MemoryEditModal';
import type {
  UserMemory,
  WorkspaceMemory,
  MemoryStats,
  WorkspaceMemoryStats,
  UserMemoryType,
  WorkspaceMemoryType,
} from '../types/memory';
import {
  USER_MEMORY_TYPE_LABELS,
  WORKSPACE_MEMORY_TYPE_LABELS,
  getUserMemoryTypeLabel,
  getWorkspaceMemoryTypeLabel,
} from '../types/memory';

type MemoryScope = 'user' | 'workspace';

export const UserMemoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, initialized } = useAuthStore();
  const { workspaces, fetchWorkspaces } = useWorkspaceStore();

  const [userMemories, setUserMemories] = useState<UserMemory[]>([]);
  const [userStats, setUserStats] = useState<MemoryStats | null>(null);
  const [workspaceMemories, setWorkspaceMemories] = useState<WorkspaceMemory[]>([]);
  const [workspaceStats, setWorkspaceStats] = useState<WorkspaceMemoryStats | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scope, setScope] = useState<MemoryScope>('user');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [editingMemory, setEditingMemory] = useState<UserMemory | WorkspaceMemory | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (initialized && !isAuthenticated) {
      navigate('/login');
    }
  }, [initialized, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspaces();
    }
  }, [fetchWorkspaces, isAuthenticated]);

  const fetchUserMemories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await memoryApi.getUserMemories();
      setUserMemories(data.memories);
      setUserStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取用户记忆失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspaceMemories = useCallback(async (workspaceId: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await memoryApi.getWorkspaceMemories(workspaceId);
      setWorkspaceMemories(data.memories);
      setWorkspaceStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取工作区记忆失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && scope === 'user') {
      fetchUserMemories();
    }
  }, [fetchUserMemories, isAuthenticated, scope]);

  useEffect(() => {
    if (isAuthenticated && scope === 'workspace' && selectedWorkspaceId) {
      fetchWorkspaceMemories(selectedWorkspaceId);
    } else if (scope === 'workspace' && !selectedWorkspaceId) {
      setWorkspaceMemories([]);
      setWorkspaceStats(null);
      setLoading(false);
    }
  }, [fetchWorkspaceMemories, isAuthenticated, scope, selectedWorkspaceId]);

  useEffect(() => {
    if (scope === 'workspace' && workspaces.length > 0 && !selectedWorkspaceId) {
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [scope, workspaces, selectedWorkspaceId]);

  const handleOpenCreateModal = () => {
    setEditMode('create');
    setEditingMemory(null);
    setEditModalOpen(true);
  };

  const handleOpenEditModal = (memory: UserMemory | WorkspaceMemory) => {
    setEditMode('edit');
    setEditingMemory(memory);
    setEditModalOpen(true);
  };

  const handleSaveMemory = async (
    memoryType: UserMemoryType | WorkspaceMemoryType,
    content: string
  ) => {
    if (scope === 'user') {
      if (editMode === 'create') {
        const newMemory = await memoryApi.createUserMemory({
          memory_type: memoryType as UserMemoryType,
          content,
        });
        setUserMemories((prev) => [newMemory, ...prev]);
        if (userStats) {
          setUserStats({
            ...userStats,
            user_memory_count: userStats.user_memory_count + 1,
            user_memory_tokens: userStats.user_memory_tokens + newMemory.token_count,
          });
        }
      } else if (editingMemory) {
        const updatedMemory = await memoryApi.updateUserMemory(editingMemory.id, { content });
        setUserMemories((prev) =>
          prev.map((m) => (m.id === editingMemory.id ? updatedMemory : m))
        );
      }
    } else if (scope === 'workspace' && selectedWorkspaceId) {
      if (editMode === 'create') {
        const newMemory = await memoryApi.createWorkspaceMemory(selectedWorkspaceId, {
          memory_type: memoryType as WorkspaceMemoryType,
          content,
        });
        setWorkspaceMemories((prev) => [newMemory, ...prev]);
        if (workspaceStats) {
          setWorkspaceStats({
            ...workspaceStats,
            workspace_memory_count: workspaceStats.workspace_memory_count + 1,
            workspace_memory_tokens: workspaceStats.workspace_memory_tokens + newMemory.token_count,
          });
        }
      } else if (editingMemory) {
        const updatedMemory = await memoryApi.updateWorkspaceMemory(
          selectedWorkspaceId,
          editingMemory.id,
          { content }
        );
        setWorkspaceMemories((prev) =>
          prev.map((m) => (m.id === editingMemory.id ? updatedMemory : m))
        );
      }
    }
  };

  const handleDelete = async (memoryId: number) => {
    if (!window.confirm('确定要删除这条记忆吗？')) return;

    setDeletingId(memoryId);
    try {
      if (scope === 'user') {
        await memoryApi.deleteUserMemory(memoryId);
        const deleted = userMemories.find((m) => m.id === memoryId);
        setUserMemories((prev) => prev.filter((m) => m.id !== memoryId));
        if (userStats && deleted) {
          setUserStats({
            ...userStats,
            user_memory_count: userStats.user_memory_count - 1,
            user_memory_tokens: userStats.user_memory_tokens - deleted.token_count,
          });
        }
      } else if (selectedWorkspaceId) {
        await memoryApi.deleteWorkspaceMemory(selectedWorkspaceId, memoryId);
        const deleted = workspaceMemories.find((m) => m.id === memoryId);
        setWorkspaceMemories((prev) => prev.filter((m) => m.id !== memoryId));
        if (workspaceStats && deleted) {
          setWorkspaceStats({
            ...workspaceStats,
            workspace_memory_count: workspaceStats.workspace_memory_count - 1,
            workspace_memory_tokens: workspaceStats.workspace_memory_tokens - deleted.token_count,
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  const currentMemories = scope === 'user' ? userMemories : workspaceMemories;
  const currentStats = scope === 'user' ? userStats : workspaceStats;
  const typeLabels = scope === 'user' ? USER_MEMORY_TYPE_LABELS : WORKSPACE_MEMORY_TYPE_LABELS;
  const getTypeLabel = (type: string) =>
    scope === 'user'
      ? getUserMemoryTypeLabel(type as UserMemoryType)
      : getWorkspaceMemoryTypeLabel(type as WorkspaceMemoryType);

  const groupedMemories = typeLabels.map((label) => ({
    ...label,
    memories: currentMemories.filter((m) => m.memory_type === label.type),
  }));

  if (!initialized) {
    return (
      <AuthorCenterLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </AuthorCenterLayout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthorCenterLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">创作记忆</h1>
              <p className="text-gray-500 mt-1">
                AI 会根据这些记忆为你提供更个性化的服务
              </p>
            </div>
            <button
              onClick={handleOpenCreateModal}
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新增记忆
              </span>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
              <button onClick={() => setError('')} className="ml-4 underline">
                关闭
              </button>
            </div>
          )}

          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-4">
              <button
                onClick={() => setScope('user')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  scope === 'user'
                    ? 'border-gray-800 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                用户记忆
              </button>
              <button
                onClick={() => setScope('workspace')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  scope === 'workspace'
                    ? 'border-gray-800 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                工作区记忆
              </button>
            </nav>
          </div>

          {scope === 'workspace' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择工作区
              </label>
              {workspaces.length === 0 ? (
                <div className="text-gray-500 text-sm">
                  暂无工作区，请先创建工作区
                </div>
              ) : (
                <select
                  value={selectedWorkspaceId || ''}
                  onChange={(e) => setSelectedWorkspaceId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {currentStats && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {scope === 'user'
                    ? (currentStats as MemoryStats).user_memory_count
                    : (currentStats as WorkspaceMemoryStats).workspace_memory_count}
                </div>
                <div className="text-sm text-gray-500">记忆条数</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {(scope === 'user'
                    ? (currentStats as MemoryStats).user_memory_tokens
                    : (currentStats as WorkspaceMemoryStats).workspace_memory_tokens
                  ).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Token 数量</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3 mb-6">
            {groupedMemories.map((group) => (
              <div key={group.type} className={`rounded-lg p-3 ${group.color}`}>
                <div className="font-medium">{group.label}</div>
                <div className="text-sm opacity-80">
                  {group.memories.length} 条
                </div>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
            </div>
          ) : scope === 'workspace' && !selectedWorkspaceId ? (
            <div className="text-center py-12 text-gray-500">
              请先选择一个工作区
            </div>
          ) : currentMemories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="mt-2">暂无记忆</p>
              <p className="text-sm mt-1">
                {scope === 'user'
                  ? '与 AI 多交流，系统会自动学习和记录你的偏好'
                  : '在此工作区的创作过程中，系统会记录项目特定的设定'}
              </p>
              <button
                onClick={handleOpenCreateModal}
                className="mt-4 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                手动添加记忆
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {currentMemories.map((memory) => {
                const typeLabel = getTypeLabel(memory.memory_type);
                return (
                  <div
                    key={memory.id}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeLabel.color}`}>
                            {typeLabel.label}
                          </span>
                          {memory.num_times > 1 && (
                            <span className="text-xs text-gray-500">
                              强化 {memory.num_times} 次
                            </span>
                          )}
                        </div>
                        <p className="text-gray-900">{memory.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>
                            更新于 {new Date(memory.updated_at).toLocaleDateString('zh-CN')}
                          </span>
                          <span>{memory.token_count} tokens</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleOpenEditModal(memory)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="编辑"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(memory.id)}
                          disabled={deletingId === memory.id}
                          className="text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors"
                          title="删除"
                        >
                          {deletingId === memory.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
            <h3 className="font-medium mb-2">关于记忆系统</h3>
            <ul className="space-y-1 text-blue-700">
              <li>• 用户记忆：跨所有工作区共享的个人偏好和习惯</li>
              <li>• 工作区记忆：针对特定项目的写作设定和风格</li>
              <li>• 记忆会在对话中自动提取和强化，也可手动添加</li>
              <li>• 记忆容量上限为 32K tokens，超出会自动清理</li>
            </ul>
          </div>
        </div>
      </div>

      <MemoryEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveMemory}
        mode={editMode}
        memoryScope={scope}
        initialMemory={editingMemory ?? undefined}
      />
    </AuthorCenterLayout>
  );
};

export default UserMemoryPage;